export type WaypointCurveMode = 'catmullRom' | 'bezier';

/**
 * Scene-level path shape.
 * - 'smooth': spline/Bézier interpolation between waypoints (the default).
 * - 'linear': straight point-to-point segments (ignores per-segment curve overrides).
 */
export type WaypointInterpolation = 'smooth' | 'linear';

export interface Vector3Like {
    x: number;
    y: number;
    z: number;
}

export interface WaypointCurveOut {
    mode?: WaypointCurveMode;
    /** Outgoing handle, relative to this waypoint's position. */
    handleOut?: Vector3Like;
    /** Incoming handle for the next waypoint, relative to the next waypoint's position. */
    handleInNext?: Vector3Like;
}

export interface WaypointPathNode {
    position: Vector3Like;
    curveOut?: WaypointCurveOut | null;
}

export interface WaypointPathSample extends Vector3Like {
    segmentIndex: number;
    t: number;
}

export interface SegmentIndices {
    prevIndex: number;
    startIndex: number;
    endIndex: number;
    nextIndex: number;
}

export interface SegmentHandles {
    handleOut: Vector3Like;
    handleInNext: Vector3Like;
}

type LoopMode = 'loop' | 'pingpong' | 'none' | boolean | undefined;

export const cloneVec3 = (v: Vector3Like): Vector3Like => ({ x: v.x, y: v.y, z: v.z });

export const addVec3 = (a: Vector3Like, b: Vector3Like): Vector3Like => ({
    x: a.x + b.x,
    y: a.y + b.y,
    z: a.z + b.z,
});

export const subVec3 = (a: Vector3Like, b: Vector3Like): Vector3Like => ({
    x: a.x - b.x,
    y: a.y - b.y,
    z: a.z - b.z,
});

export const scaleVec3 = (v: Vector3Like, scale: number): Vector3Like => ({
    x: v.x * scale,
    y: v.y * scale,
    z: v.z * scale,
});

export const lerpVec3 = (a: Vector3Like, b: Vector3Like, t: number): Vector3Like => ({
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
});

export function isLoopingMode(loopMode: LoopMode): boolean {
    return loopMode === true || loopMode === 'loop';
}

export function getPathSegmentCount(nodes: WaypointPathNode[], loopMode: LoopMode): number {
    if (nodes.length < 2) return 0;
    return isLoopingMode(loopMode) ? nodes.length : nodes.length - 1;
}

export function getSegmentIndices(nodes: WaypointPathNode[], segmentIndex: number, loopMode: LoopMode): SegmentIndices | null {
    const count = nodes.length;
    const segmentCount = getPathSegmentCount(nodes, loopMode);
    if (count < 2 || segmentIndex < 0 || segmentIndex >= segmentCount) return null;

    const looping = isLoopingMode(loopMode);
    const wrap = (i: number) => ((i % count) + count) % count;
    const startIndex = looping ? wrap(segmentIndex) : segmentIndex;
    const endIndex = looping ? wrap(segmentIndex + 1) : segmentIndex + 1;

    return {
        prevIndex: looping ? wrap(segmentIndex - 1) : Math.max(segmentIndex - 1, 0),
        startIndex,
        endIndex,
        nextIndex: looping ? wrap(segmentIndex + 2) : Math.min(segmentIndex + 2, count - 1),
    };
}

export function getVirtualBezierHandles(nodes: WaypointPathNode[], segmentIndex: number, loopMode: LoopMode): SegmentHandles | null {
    const indices = getSegmentIndices(nodes, segmentIndex, loopMode);
    if (!indices) return null;

    const p0 = nodes[indices.prevIndex].position;
    const p1 = nodes[indices.startIndex].position;
    const p2 = nodes[indices.endIndex].position;
    const p3 = nodes[indices.nextIndex].position;

    return {
        handleOut: scaleVec3(subVec3(p2, p0), 1 / 6),
        handleInNext: scaleVec3(subVec3(p1, p3), 1 / 6),
    };
}

export function isBezierSegment(nodes: WaypointPathNode[], segmentIndex: number, loopMode: LoopMode): boolean {
    const indices = getSegmentIndices(nodes, segmentIndex, loopMode);
    if (!indices) return false;
    return nodes[indices.startIndex].curveOut?.mode === 'bezier';
}

export function getSegmentHandles(nodes: WaypointPathNode[], segmentIndex: number, loopMode: LoopMode): SegmentHandles | null {
    const indices = getSegmentIndices(nodes, segmentIndex, loopMode);
    if (!indices) return null;

    const virtualHandles = getVirtualBezierHandles(nodes, segmentIndex, loopMode);
    if (!virtualHandles) return null;

    const curveOut = nodes[indices.startIndex].curveOut;
    if (curveOut?.mode !== 'bezier') return virtualHandles;

    return {
        handleOut: curveOut.handleOut ? cloneVec3(curveOut.handleOut) : virtualHandles.handleOut,
        handleInNext: curveOut.handleInNext ? cloneVec3(curveOut.handleInNext) : virtualHandles.handleInNext,
    };
}

export function sampleCatmullRom(p0: Vector3Like, p1: Vector3Like, p2: Vector3Like, p3: Vector3Like, t: number): Vector3Like {
    const t2 = t * t;
    const t3 = t2 * t;
    return {
        x: 0.5 * ((2 * p1.x) + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
        y: 0.5 * ((2 * p1.y) + (-p0.y + p2.y) * t + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
        z: 0.5 * ((2 * p1.z) + (-p0.z + p2.z) * t + (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * t2 + (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * t3),
    };
}

export function sampleCubicBezier(p0: Vector3Like, p1: Vector3Like, p2: Vector3Like, p3: Vector3Like, t: number): Vector3Like {
    const invT = 1 - t;
    const invT2 = invT * invT;
    const invT3 = invT2 * invT;
    const t2 = t * t;
    const t3 = t2 * t;
    return {
        x: invT3 * p0.x + 3 * invT2 * t * p1.x + 3 * invT * t2 * p2.x + t3 * p3.x,
        y: invT3 * p0.y + 3 * invT2 * t * p1.y + 3 * invT * t2 * p2.y + t3 * p3.y,
        z: invT3 * p0.z + 3 * invT2 * t * p1.z + 3 * invT * t2 * p2.z + t3 * p3.z,
    };
}

export function sampleWaypointPathSegment(nodes: WaypointPathNode[], segmentIndex: number, t: number, loopMode: LoopMode, interpolation: WaypointInterpolation = 'smooth'): Vector3Like | null {
    const indices = getSegmentIndices(nodes, segmentIndex, loopMode);
    if (!indices) return null;

    const p0 = nodes[indices.prevIndex].position;
    const p1 = nodes[indices.startIndex].position;
    const p2 = nodes[indices.endIndex].position;
    const p3 = nodes[indices.nextIndex].position;

    // Linear mode: straight segment between the two waypoints, ignoring any
    // per-segment curve overrides.
    if (interpolation === 'linear') {
        return lerpVec3(p1, p2, t);
    }

    if (!isBezierSegment(nodes, segmentIndex, loopMode)) {
        return sampleCatmullRom(p0, p1, p2, p3, t);
    }

    const handles = getSegmentHandles(nodes, segmentIndex, loopMode);
    if (!handles) return null;

    return sampleCubicBezier(
        p1,
        addVec3(p1, handles.handleOut),
        addVec3(p2, handles.handleInNext),
        p2,
        t
    );
}

export function generateWaypointPathSamples(nodes: WaypointPathNode[], subdivisionsPerSegment: number, loopMode: LoopMode, interpolation: WaypointInterpolation = 'smooth'): WaypointPathSample[] {
    if (nodes.length < 2) {
        return nodes.map((node, index) => ({ ...cloneVec3(node.position), segmentIndex: index, t: 0 }));
    }

    const samples: WaypointPathSample[] = [];
    const segmentCount = getPathSegmentCount(nodes, loopMode);
    // Straight segments need no subdivision — two endpoints fully describe them.
    const subdivisions = interpolation === 'linear' ? 1 : Math.max(1, subdivisionsPerSegment);

    for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex++) {
        for (let step = 0; step < subdivisions; step++) {
            const t = step / subdivisions;
            const sample = sampleWaypointPathSegment(nodes, segmentIndex, t, loopMode, interpolation);
            if (sample) {
                samples.push({ ...sample, segmentIndex, t });
            }
        }
    }

    const finalIndex = isLoopingMode(loopMode) ? 0 : nodes.length - 1;
    samples.push({ ...cloneVec3(nodes[finalIndex].position), segmentIndex: Math.max(0, segmentCount - 1), t: 1 });
    return samples;
}
